import * as Sentry from "@sentry/react";

import { Form, FormikProps } from "formik";

import { Paragraph } from "App/global-style";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { NavigationButtons } from "Components/NavigationButtons";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { useFeatureToggle } from "Store/featureToggles/featureToggleHooks";
import { FeatureToggle } from "Store/featureToggles/featureToggleTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { ElectricScooterCoverage } from "Store/interfaces/products/coverage";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { ReduxState } from "Store/rootReducer";
import { TextLanguage } from "Store/texts/interfaces";
import { mapValidationErrors } from "Utils/formUtils";
import { isNull } from "Utils/functions";
import React from "react";
import { useSelector } from "react-redux";
import { ELECTRIC_SCOOTER_PRICE_STEP } from "..";
import { ElectricScooterTexts } from "../interface";
import { Coverages } from "./Coverages";
import { ElectricScooterProductPriceFormValues } from "./interface";
import { validationSchema } from "./validation";

interface Props {
  readonly texts: ElectricScooterTexts;
  readonly lang: TextLanguage;
}

export const ElectricScooterProductPrice = (
  props: Props,
): React.ReactElement => {
  const disableKaskoForElectricScooter = useFeatureToggle(
    FeatureToggle.SSX_513,
  );
  const { texts, lang } = props;
  const addProductToCart = useAddProductToCartHandler(
    ELECTRIC_SCOOTER_PRICE_STEP.id,
  );

  const initialFormState = useFormState<ElectricScooterProductPriceFormValues>(
    ELECTRIC_SCOOTER_PRICE_STEP.id,
    {
      coverage: disableKaskoForElectricScooter
        ? ElectricScooterCoverage.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE
        : ElectricScooterCoverage.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE,
    },
  );

  const { addProductToCart: submitState } = useSelector(
    (state: ReduxState) => state,
  );

  const handleSubmit = (
    formValues: ElectricScooterProductPriceFormValues,
  ): void => {
    const productToAdd = {
      coverage: formValues.coverage,
      productType: INSURANCE_NAME_VALUE.ELECTRIC_SCOOTER,
    };
    addProductToCart(formValues, [productToAdd]);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.ELECTRIC_SCOOTER}`,
        );
      }}
    >
      <ProductPriceTile
        header={texts.tile.productPrice.heading[lang]}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {(formProps: FormikProps<ElectricScooterProductPriceFormValues>) => (
          <Form>
            <Paragraph>{texts.tile.productPrice.ingress[lang]}</Paragraph>
            <Coverages texts={texts} lang={lang} />

            {!isNull(submitState.error) && (
              <SystemErrorAlert alertId="addProductToCart" />
            )}

            <ErrorsSummedUp
              errors={mapValidationErrors(
                formProps.errors,
                texts.details,
                lang,
              )}
              isVisible
            />

            <NavigationButtons isUpsell isLoading={submitState.isLoading} />
          </Form>
        )}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
